import React, { useRef, useState } from 'react'
import { View, TouchableOpacity, StyleSheet, ScrollView } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { Icon, Text } from '@components'
import { Theme } from '@theme'
import { Profile, useCreationStackIntegration, usePaginateKeepContacts } from '@hooks'
import { useScreen } from '@hooks'
import { useLanguage } from '@context'
import { QuickKeepContactDropdown } from './QuickKeepContactDropdown'

interface GhostFilterButtonsProps {
  profile?: Profile
  onHashtagsPress?: () => void
  onCategoriesPress?: () => void
}

export const GhostFilterButtons: React.FC<GhostFilterButtonsProps> = ({
  profile,
  onHashtagsPress,
  onCategoriesPress,
}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const { isDesktop } = useScreen()
  const styles = getStyle(theme, isDesktop)
  const navigation = useNavigation()
  const { showKeepContactCreation, isCreationStackEnabled } = useCreationStackIntegration()

  const [isKeepContactDropdownVisible, setIsKeepContactDropdownVisible] = useState(false)
  const [dropdownAnchorLayout, setDropdownAnchorLayout] = useState<{
    x: number
    y: number
    width: number
    height: number
  } | null>(null)
  const keepContactButtonRef = useRef<View>(null)

  const contactId = profile?.accountId || profile?.id

  const { flatData: keepContactsForContact } = usePaginateKeepContacts({
    contactAccountId: contactId || undefined,
  })
  const existingKeepContact =
    keepContactsForContact?.find((k) => k.contactAccountId === contactId) ?? null

  const goToContexte = () => {
    ;(navigation as any).navigate('Profile', {
      screen: 'ProfileContextes',
      params: {
        profileId: contactId,
      },
    })
  }

  const goToKeepContactFull = () => {
    if (isCreationStackEnabled) {
      showKeepContactCreation({
        isCreationStack: true,
        keepContactId: existingKeepContact?.id,
        contactId,
      })
    } else {
      ;(navigation as any).navigate('Menu', {
        screen: 'KeepContact',
        params: {
          screen: 'AddKeep',
          params: {
            contactId,
            keepContactId: existingKeepContact?.id,
          },
        },
      })
    }
  }

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.container}
      style={styles.scroll}
    >
      <View ref={keepContactButtonRef} collapsable={false}>
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => {
            if (isDesktop && keepContactButtonRef.current?.measureInWindow) {
              keepContactButtonRef.current.measureInWindow((x, y, w, h) => {
                setDropdownAnchorLayout({ x, y, width: w, height: h })
                setIsKeepContactDropdownVisible(true)
              })
            } else {
              setDropdownAnchorLayout(null)
              setIsKeepContactDropdownVisible(true)
            }
          }}
        >
          <Icon
            name="keepContact"
            size={theme.iconSize.extraSmall}
            color={theme.colors.secondary}
          />
          <Text type="bodySmall" color={theme.colors.primary}>
            {t('ghostProfile.keepContact')}
          </Text>
        </TouchableOpacity>
      </View>
      <TouchableOpacity style={styles.actionButton} onPress={goToContexte}>
        <Icon
          name="taggedPictures"
          size={theme.iconSize.extraSmall}
          color={theme.colors.secondary}
        />
        <Text type="bodySmall" color={theme.colors.primary}>
          {t('ghostProfile.context')}
        </Text>
      </TouchableOpacity>

      <TouchableOpacity style={styles.actionButton} onPress={onHashtagsPress}>
        <Icon name="htag" size={theme.iconSize.extraSmall} color={theme.colors.secondary} />
        <Text type="bodySmall" color={theme.colors.primary}>
          {t('ghostProfile.hashtag')}
        </Text>
      </TouchableOpacity>

      <TouchableOpacity style={styles.actionButton} onPress={onCategoriesPress}>
        <Icon name="pin" size={theme.iconSize.extraSmall} color={theme.colors.secondary} />
        <Text type="bodySmall" color={theme.colors.primary}>
          {t('ghostProfile.categories')}
        </Text>
      </TouchableOpacity>
      <QuickKeepContactDropdown
        visible={isKeepContactDropdownVisible}
        onClose={() => {
          setIsKeepContactDropdownVisible(false)
          setDropdownAnchorLayout(null)
        }}
        contactAccountId={contactId || ''}
        contactName={`${profile?.firstName || ''} ${profile?.lastName || ''}`.trim()}
        existingKeepContact={existingKeepContact}
        anchorLayout={isDesktop ? dropdownAnchorLayout : null}
        onCustomize={goToKeepContactFull}
      />
    </ScrollView>
  )
}

const getStyle = (theme: Theme, isDesktop: boolean) =>
  StyleSheet.create({
    scroll: {
      flexGrow: 0,
      flexShrink: 1,
      maxWidth: '100%',
    },
    container: {
      flexDirection: 'row',
      justifyContent: 'space-around',
      alignItems: 'center',
      marginBottom: theme.spacing.sm,
      gap: theme.spacing.sm,
      paddingHorizontal: theme.spacing.xs,
    },
    actionButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 2,
      paddingHorizontal: isDesktop ? theme.spacing.lg : undefined,
      borderRadius: theme.spacing.lg,
    },
  })
